// Comprehensive Offline Manager for AgroSense
// Coordinates all offline functionality and data synchronization

import { advancedOfflineStorage, swManager } from './advancedOfflineStorage';

interface NetworkStatus {
  isOnline: boolean;
  connectionType: string;
  effectiveType: string;
  downlink: number;
  rtt: number;
}

interface OfflineConfig {
  enableBackgroundSync: boolean;
  maxRetries: number;
  syncInterval: number;
  cacheStrategy: 'aggressive' | 'conservative' | 'minimal';
  dataRetentionDays: number;
}

class OfflineManager {
  private networkStatus: NetworkStatus;
  private config: OfflineConfig;
  private syncInProgress: boolean = false;
  private lastSyncTime: number = 0;
  private eventListeners: Map<string, Function[]> = new Map();

  constructor() {
    this.networkStatus = {
      isOnline: navigator.onLine,
      connectionType: 'unknown',
      effectiveType: 'unknown',
      downlink: 0,
      rtt: 0
    };

    this.config = {
      enableBackgroundSync: true,
      maxRetries: 3,
      syncInterval: 30000, // 30 seconds
      cacheStrategy: 'aggressive',
      dataRetentionDays: 30
    };

    this.init();
  }

  private async init(): Promise<void> {
    // Initialize service worker
    await swManager.register();
    
    // Setup network monitoring
    this.setupNetworkMonitoring();
    
    // Setup periodic sync
    this.setupPeriodicSync();
    
    // Setup storage cleanup
    this.setupStorageCleanup();
    
    // Load configuration
    await this.loadConfig();
    
    console.log('OfflineManager initialized');
  }

  private setupNetworkMonitoring(): void {
    // Basic online/offline detection
    window.addEventListener('online', () => {
      this.networkStatus.isOnline = true;
      this.emit('network-status-changed', this.networkStatus);
      this.handleOnline();
    });

    window.addEventListener('offline', () => {
      this.networkStatus.isOnline = false;
      this.emit('network-status-changed', this.networkStatus);
      this.handleOffline();
    });

    // Advanced connection monitoring
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      
      const updateConnectionInfo = () => {
        this.networkStatus = {
          ...this.networkStatus,
          connectionType: connection.type || 'unknown',
          effectiveType: connection.effectiveType || 'unknown',
          downlink: connection.downlink || 0,
          rtt: connection.rtt || 0
        };
        this.emit('connection-changed', this.networkStatus);
      };

      connection.addEventListener('change', updateConnectionInfo);
      updateConnectionInfo();
    }
  }

  private setupPeriodicSync(): void {
    setInterval(() => {
      if (this.networkStatus.isOnline && !this.syncInProgress) {
        this.performBackgroundSync();
      }
    }, this.config.syncInterval);
  }

  private setupStorageCleanup(): void {
    // Clean up expired data daily
    setInterval(() => {
      this.cleanupExpiredData();
    }, 24 * 60 * 60 * 1000);
  }

  private async loadConfig(): Promise<void> {
    try {
      const savedConfig = await advancedOfflineStorage.read<OfflineConfig>('settings', 'offline-config');
      if (savedConfig) {
        this.config = { ...this.config, ...savedConfig };
      }
    } catch (error) {
      console.warn('Failed to load offline config:', error);
    }
  }

  private async saveConfig(): Promise<void> {
    try {
      await advancedOfflineStorage.update('settings', {
        key: 'offline-config',
        ...this.config,
        userId: 'system'
      });
    } catch (error) {
      console.warn('Failed to save offline config:', error);
    }
  }

  // Event system
  on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  // Network event handlers
  private async handleOnline(): Promise<void> {
    console.log('Network connection restored');
    
    // Trigger immediate sync
    await this.performBackgroundSync();
    
    // Update cached data
    await this.refreshCriticalData();
    
    this.emit('online');
  }

  private handleOffline(): void {
    console.log('Network connection lost - entering offline mode');
    this.emit('offline');
  }

  // Data management
  async storeData<T>(key: string, data: T, options: {
    storeName?: string;
    expiry?: number;
    syncRequired?: boolean;
  } = {}): Promise<void> {
    const {
      storeName = 'apiCache',
      expiry = 5 * 60 * 1000, // 5 minutes default
      syncRequired = false
    } = options;

    try {
      if (storeName === 'apiCache') {
        await advancedOfflineStorage.cacheData(key, data, expiry);
      } else {
        await advancedOfflineStorage.update(storeName, { id: key, ...data });
      }

      if (syncRequired && !this.networkStatus.isOnline) {
        await this.queueForSync(key, data, 'UPDATE');
      }

      this.emit('data-stored', { key, storeName });
    } catch (error) {
      console.error('Failed to store data:', error);
      throw error;
    }
  }

  async getData<T>(key: string, options: {
    storeName?: string;
    fallback?: T;
    maxAge?: number;
  } = {}): Promise<T | null> {
    const {
      storeName = 'apiCache',
      fallback = null,
      maxAge
    } = options;

    try {
      let data: T | null;

      if (storeName === 'apiCache') {
        data = await advancedOfflineStorage.getCachedData<T>(key);
      } else {
        data = await advancedOfflineStorage.read<T>(storeName, key);
      }

      // Check age if specified
      if (data && maxAge && (data as any).timestamp) {
        const age = Date.now() - (data as any).timestamp;
        if (age > maxAge) {
          data = null;
        }
      }

      return data || fallback;
    } catch (error) {
      console.error('Failed to get data:', error);
      return fallback;
    }
  }

  async deleteData(key: string, storeName: string = 'apiCache'): Promise<void> {
    try {
      await advancedOfflineStorage.delete(storeName, key);
      this.emit('data-deleted', { key, storeName });
    } catch (error) {
      console.error('Failed to delete data:', error);
      throw error;
    }
  }

  // Sync management
  private async queueForSync(key: string, data: any, type: 'CREATE' | 'UPDATE' | 'DELETE'): Promise<void> {
    await advancedOfflineStorage.addToSyncQueue({
      type,
      endpoint: this.getEndpointForKey(key),
      data,
      priority: this.getPriorityForKey(key)
    });
  }

  private getEndpointForKey(key: string): string {
    // Map keys to API endpoints
    if (key.startsWith('farm-')) return `/api/farms/${key.replace('farm-', '')}`;
    if (key.startsWith('report-')) return `/api/reports/${key.replace('report-', '')}`;
    if (key.startsWith('user-')) return `/api/users/${key.replace('user-', '')}`;
    return `/api/data/${key}`;
  }

  private getPriorityForKey(key: string): 'high' | 'medium' | 'low' {
    if (key.startsWith('report-') || key.startsWith('alert-')) return 'high';
    if (key.startsWith('farm-') || key.startsWith('user-')) return 'medium';
    return 'low';
  }

  async performBackgroundSync(): Promise<void> {
    if (this.syncInProgress || !this.networkStatus.isOnline) {
      return;
    }

    this.syncInProgress = true;
    this.emit('sync-started');

    try {
      // Get current user
      const userId = this.getCurrentUserId();
      if (userId) {
        await advancedOfflineStorage.syncUserData(userId);
      }

      this.lastSyncTime = Date.now();
      this.emit('sync-completed', { timestamp: this.lastSyncTime });
    } catch (error) {
      console.error('Background sync failed:', error);
      this.emit('sync-failed', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  private getCurrentUserId(): string | null {
    // Get from localStorage or other auth storage
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user).id : null;
  }

  // Cache management
  async refreshCriticalData(): Promise<void> {
    const criticalEndpoints = [
      '/api/weather-data?location=Maseru,Lesotho',
      '/api/market-prices',
      '/api/pest-weather-alerts'
    ];

    const refreshPromises = criticalEndpoints.map(async (endpoint) => {
      try {
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          await this.storeData(endpoint, data, { expiry: 10 * 60 * 1000 }); // 10 minutes
        }
      } catch (error) {
        console.warn(`Failed to refresh ${endpoint}:`, error);
      }
    });

    await Promise.allSettled(refreshPromises);
    this.emit('critical-data-refreshed');
  }

  async preloadData(endpoints: string[]): Promise<void> {
    if (!this.networkStatus.isOnline) {
      console.warn('Cannot preload data while offline');
      return;
    }

    const preloadPromises = endpoints.map(async (endpoint) => {
      try {
        const response = await fetch(endpoint);
        if (response.ok) {
          const data = await response.json();
          await this.storeData(endpoint, data);
        }
      } catch (error) {
        console.warn(`Failed to preload ${endpoint}:`, error);
      }
    });

    await Promise.allSettled(preloadPromises);
    this.emit('data-preloaded', { endpoints });
  }

  // Storage cleanup
  private async cleanupExpiredData(): Promise<void> {
    try {
      await advancedOfflineStorage.cleanExpiredData();
      
      // Clean old sync queue items
      const cutoffTime = Date.now() - (this.config.dataRetentionDays * 24 * 60 * 60 * 1000);
      const oldSyncItems = await advancedOfflineStorage.getAll('syncQueue', 'timestamp');
      
      for (const item of oldSyncItems) {
        if (item.timestamp < cutoffTime) {
          await advancedOfflineStorage.delete('syncQueue', item.id);
        }
      }

      this.emit('cleanup-completed');
    } catch (error) {
      console.error('Cleanup failed:', error);
    }
  }

  // Configuration
  updateConfig(newConfig: Partial<OfflineConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
    this.emit('config-updated', this.config);
  }

  getConfig(): OfflineConfig {
    return { ...this.config };
  }

  // Status and diagnostics
  getNetworkStatus(): NetworkStatus {
    return { ...this.networkStatus };
  }

  async getStorageInfo(): Promise<{
    usage: number;
    quota: number;
    stats: any;
    lastSync: number;
  }> {
    const stats = await advancedOfflineStorage.getStorageStats();
    
    let usage = 0;
    let quota = 0;
    
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      usage = estimate.usage || 0;
      quota = estimate.quota || 0;
    }

    return {
      usage,
      quota,
      stats,
      lastSync: this.lastSyncTime
    };
  }

  isOfflineCapable(): boolean {
    return 'serviceWorker' in navigator && 'indexedDB' in window;
  }

  // Smart request handling
  async smartFetch(url: string, options: RequestInit = {}): Promise<Response> {
    // Try network first if online and good connection
    if (this.networkStatus.isOnline && this.hasGoodConnection()) {
      try {
        const response = await fetch(url, {
          ...options,
          signal: AbortSignal.timeout(5000) // 5 second timeout
        });
        
        if (response.ok) {
          // Cache successful responses
          const data = await response.clone().json();
          await this.storeData(url, data);
        }
        
        return response;
      } catch (error) {
        console.warn('Network request failed, trying cache:', error);
      }
    }

    // Try cache
    const cachedData = await this.getData(url);
    if (cachedData) {
      return new Response(JSON.stringify(cachedData), {
        status: 200,
        headers: { 'Content-Type': 'application/json', 'X-Cache': 'offline' }
      });
    }

    // If offline and no cache, queue for later
    if (!this.networkStatus.isOnline && options.method !== 'GET') {
      await this.queueForSync(url, options.body, options.method as any || 'POST');
      return new Response(JSON.stringify({ queued: true }), {
        status: 202,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    throw new Error('No network connection and no cached data available');
  }

  private hasGoodConnection(): boolean {
    // Consider connection good if effective type is 3g or better
    return ['4g', '3g'].includes(this.networkStatus.effectiveType) ||
           this.networkStatus.downlink > 1.5; // > 1.5 Mbps
  }

  // Export/Import for backup
  async exportOfflineData(): Promise<string> {
    return await advancedOfflineStorage.exportData();
  }

  async importOfflineData(data: string): Promise<void> {
    await advancedOfflineStorage.importData(data);
    this.emit('data-imported');
  }

  // Cleanup and destroy
  destroy(): void {
    // Remove event listeners
    this.eventListeners.clear();
    
    // Clear intervals would go here if we stored references
    console.log('OfflineManager destroyed');
  }
}

// Singleton instance
export const offlineManager = new OfflineManager();

// React hook for offline functionality
export function useOfflineManager() {
  const [networkStatus, setNetworkStatus] = React.useState(offlineManager.getNetworkStatus());
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleNetworkChange = (status: NetworkStatus) => {
      setNetworkStatus(status);
      setIsOnline(status.isOnline);
    };

    offlineManager.on('network-status-changed', handleNetworkChange);
    
    return () => {
      offlineManager.off('network-status-changed', handleNetworkChange);
    };
  }, []);

  return {
    networkStatus,
    isOnline,
    storeData: offlineManager.storeData.bind(offlineManager),
    getData: offlineManager.getData.bind(offlineManager),
    smartFetch: offlineManager.smartFetch.bind(offlineManager),
    performSync: offlineManager.performBackgroundSync.bind(offlineManager),
    getStorageInfo: offlineManager.getStorageInfo.bind(offlineManager)
  };
}